import { HeatPumpPort, PortConfigHeatPump } from '../../types/ports'
import { ID } from '../../types/models'
import { PortControl } from './PortControl'
import { OptimizerControlPortControl } from './OptimizerControlPortControl'
import { HeatPumpStatusView } from '../../components/HeatPumpStatusView'
import { HEAT_PUMP_CONF_DEFAULTS, HEAT_PUMP_OPTIMIZER_INSTANCES } from '../appliances/heat-pump-defaults'

/**
 * Controller for a heat pump on port 0x23 (PORT_HEATPUMP, spp_heatpump.h):
 * Modbus-connected air-to-water / ground source unit, e.g. Midea M-Thermal.
 *
 * Pilot: the port is visible only to users with the HEAT_PUMP_APPLIANCE feature.
 */
export class HeatPumpPortControl extends PortControl<HeatPumpPort, PortConfigHeatPump> {

  getDescription(): string { return this.app.t('Heat pump connected over Modbus') }

  getTypeName(): string { return this.app.t('PortType-Heat Pump') }

  getConfigDefaults(): PortConfigHeatPump {
    return { ...HEAT_PUMP_CONF_DEFAULTS }
  }

  get isVisible() {
    return this.app.userHas('HEAT_PUMP_APPLIANCE')
  }

  /**
   * The optimizer control that plans this heat pump: the one already targeting
   * this port, else the first free one of HEAT_PUMP_OPTIMIZER_INSTANCES (one
   * that targets no port; the server refuses a control port another appliance owns).
   */
  optimizerControl(): OptimizerControlPortControl | null {
    const isControl = (ctrl: PortControl<any, any>) => ctrl.port.type === 'OPTIMIZER_CONTROL'
    const own = this.deviceControl.findPortBy(ctrl =>
      isControl(ctrl) && (ctrl as OptimizerControlPortControl).getConfig('port') === this.port.port_number)
    if (own) return own as OptimizerControlPortControl

    for (const portNumber of HEAT_PUMP_OPTIMIZER_INSTANCES) {
      const free = this.deviceControl.findPortBy(ctrl => {
        if (!isControl(ctrl) || ctrl.port.port_number !== portNumber) return false
        const target = (ctrl as OptimizerControlPortControl).getConfig('port')
        return !target || target === '0/0'
      })
      if (free) return free as OptimizerControlPortControl
    }
    return null
  }

  getAssociatedPortIDs(): ID[] {
    const opt = this.optimizerControl()
    if (!opt) {
      throw new Error(`Cannot find a free optimizer control for port #${this.port.id}.`)
    }
    return [this.port.id as ID, opt.port.id as ID]
  }

  infoTop(): React.ReactNode | undefined {
    return <HeatPumpStatusView sx={{ mt: 3 }} values={this.port.values}/>
  }
}
